import CruciblePiece from './cruciblePiece';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';
import * as config from '../config.json';
import { CentralPieceConfig } from '../config';
import Proton from 'three.proton.js';
import CrucibleLoader from '../crucibleLoader';
import PRNG from '../PRNG';
import CrucibleColors from '../crucibleColors';

export class CentralPiece extends CruciblePiece {
  protected _config: CentralPieceConfig;
  protected _proton: any;
  protected _tier: number;
  protected _level: number;
  protected _isVFXLoaded: boolean;

  protected _gemMain: THREE.Object3D;
  protected _gemPieces: THREE.Object3D | null;
  protected _gemGlow: THREE.Object3D;
  protected _animationTime: number;
  protected _gemPiecesTime: number;

  protected _gemMainMaterial: THREE.MeshStandardMaterial;
  protected _gemGlowMaterial: THREE.MeshStandardMaterial;
  protected _emissiveIntensity: number;
  protected _emissiveAnimationSpeed: number;
  protected _emissiveRange: number;
  protected _emissiveAngle: number;

  constructor() {
    super();
    this._config = config.centralPiece;
    this._proton = null;
    this._tier = 0;
    this._level = 0;
    this._isVFXLoaded = false;

    this._animationTime = 0;
    this._gemMain = new THREE.Object3D();
    this._gemMainMaterial = new THREE.MeshStandardMaterial();

    this._gemPieces = null;
    this._gemPiecesTime = 0;

    this._gemGlow = new THREE.Object3D();
    this._gemGlowMaterial = new THREE.MeshStandardMaterial();

    this._emissiveIntensity = 0;
    this._emissiveAnimationSpeed = 0.6;
    this._emissiveRange = 4;
    this._emissiveAngle = 0;
  }

  setup(gltf: GLTF) {
    this._gemMain = new THREE.Object3D();
    this._gemGlow = new THREE.Object3D();
    this._gemPieces = null;
    this._proton = new Proton();
    this._tier = 0;
    this._level = 0;
    this._isVFXLoaded = false;

    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2);
    this.group.rotation.y -= Math.PI / 2;

    this.group.traverse((node: any) => {
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;

        if (node.material.map) {
          node.material.map.anisotropy = 16;
        }
      }

      if (node.name === this._config.gemGlowNodeName) {
        this._gemGlow = node;
        this._gemGlowMaterial = node.children[0].material;
      }

      if (node.name === this._config.gemPiecesNodeName) {
        this._gemPieces = node;
      }

      if (node.name === this._config.gemNodeName) {
        this._gemMain = node;
        this._gemMain.traverse((node) => {
          if (node instanceof THREE.Mesh) {
            this._gemMainMaterial = node.material;
            this._emissiveIntensity = node.material.emissiveIntensity;
          }
        });
      }
    });

    // Remove the glow and add back depending on the user's Level
    this._gemGlow.parent?.remove(this._gemGlow);
  }

  update(
    crucibleLevel: number,
    crucibleTier: number,
    color: THREE.Color,
    scene: THREE.Scene,
    loader: CrucibleLoader,
    crucibleColors: CrucibleColors,
    prng: PRNG
  ) {
    this._level = crucibleLevel;
    this._tier = crucibleTier;

    if (this._tier >= 1) {
      this.group.children[0].add(this._gemGlow);
    }

    if (this._tier >= 2) {
      this._proton.addEmitter(
        this._createTier2EffectEmitter(prng, loader, color)
      );
    }

    if (this._tier >= 3) {
      this._proton.addEmitter(
        this._createTier3EffectEmitter(prng, loader, color, crucibleColors)
      );
    }

    this._proton.addRender(new Proton.SpriteRender(scene));
    this._isVFXLoaded = true;
  }

  animate(timeElapsed: number) {
    this._animationTime += this._config.gemMoveSpeed * timeElapsed;

    this._animateGemPosition(timeElapsed);
    this._animateGemEmissive(Math.max(this._emissiveRange, this._tier + 1));

    if (this._gemPieces) this._animateGemPieces(timeElapsed);

    // Animate special effects depending on Crucible Level / Tier
    if (this._tier >= 1) this._animateGemBaseGlow(this._tier);

    if (this._isVFXLoaded) this._proton.update();
  }

  _animateGemPosition(timeElapsed: number) {
    this._gemMain.position.y =
      Math.sin(this._animationTime) * this._config.gemMoveRange;
    this._gemMain.rotation.y += this._config.gemMoveSpeed * timeElapsed;
  }

  _animateGemEmissive(range: number) {
    this._gemMainMaterial.emissiveIntensity =
      this._emissiveIntensity + Math.abs(Math.sin(this._animationTime) * range);
  }

  _animateGemBaseGlow(range: number) {
    this._gemGlow.scale.y = 1 + Math.abs(Math.sin(this._animationTime) * range);
    this._gemGlowMaterial.emissiveIntensity =
      1 + Math.abs(Math.sin(this._animationTime) * range);
  }

  _animateGemPieces(timeElapsed: number) {
    if (this._gemPieces === null) return;

    this._gemPiecesTime += this._config.gemPiecesMoveSpeed * timeElapsed;

    for (let i = 0; i < this._gemPieces.children.length; i++) {
      this._gemPieces.children[i].position.y = Math.abs(
        Math.sin(this._gemPiecesTime * i) * this._config.gemPiecesMoveRange
      );

      let direction = i % 2 === 0 ? 1 : -1;
      this._gemPieces.children[i].rotation.y +=
        i * 0.001 * timeElapsed * direction;
    }
  }

  _createTier2EffectEmitter(
    prng: PRNG,
    loader: CrucibleLoader,
    originalColor: THREE.Color
  ) {
    const color = new THREE.Color(originalColor);
    color.offsetHSL(0, 0.35, 0.2);

    // Create sprite for effect
    const index = prng.randomInt(
      'levelup',
      config.crucibleLevelConfig.particleTextures.length
    );
    const spriteUrl = config.crucibleLevelConfig.particleTextures[index];
    const sprite = this._createSprite(loader, spriteUrl, color);

    // Create particle emitter
    const emitter = new Proton.Emitter();
    emitter.rate = new Proton.Rate(
      new Proton.Span(8, 12),
      new Proton.Span(0.1, 0.4)
    );

    emitter.addInitialize(new Proton.Mass(1));
    emitter.addInitialize(new Proton.Radius(0.4, 0.8));
    emitter.addInitialize(new Proton.Life(1, 5));
    emitter.addInitialize(new Proton.Body(sprite));

    const protonPosition = new Proton.Position();
    protonPosition.addZone(new Proton.BoxZone(2.3, 0.2, 2.3));
    emitter.addInitialize(protonPosition);

    emitter.addInitialize(
      new Proton.Velocity(0, new Proton.Vector3D(0, 1, 0), 90)
    );

    emitter.addBehaviour(new Proton.Rotate('random', 'random'));
    emitter.addBehaviour(new Proton.Gravity(-0.008));
    emitter.addBehaviour(new Proton.Scale(1, 0.5));
    emitter.addBehaviour(new Proton.Alpha(1, 0, Infinity, Proton.easeInQuart));

    emitter.p.x = 0;
    emitter.p.y = 3;
    emitter.emit();

    return emitter;
  }

  _createTier3EffectEmitter(
    prng: PRNG,
    loader: CrucibleLoader,
    color: THREE.Color,
    crucibleColors: CrucibleColors
  ) {
    // Create sprite for effect
    const index = prng.randomInt(
      'levelup',
      config.crucibleLevelConfig.particleTextures.length
    );
    const spriteUrl = config.crucibleLevelConfig.particleTextures[index];
    const sprite = this._createSprite(loader, spriteUrl, color);

    // Create particle emitter
    const emitter = new Proton.Emitter();
    emitter.rate = new Proton.Rate(
      new Proton.Span(5, 10),
      new Proton.Span(0.2, 0.6)
    );

    emitter.addInitialize(new Proton.Mass(1));
    emitter.addInitialize(new Proton.Radius(0.8, 1.8));
    emitter.addInitialize(new Proton.Life(1, 4));
    emitter.addInitialize(new Proton.Body(sprite));
    // emitter.addInitialize(new Proton.Position(new Proton.PointZone(0, 6, 0)));
    emitter.addInitialize(new Proton.Position(new Proton.BoxZone(3, 4, 3)));
    emitter.addInitialize(
      new Proton.Velocity(0.8, new Proton.Vector3D(0, 1, 0), 180)
    );

    emitter.addBehaviour(new Proton.Rotate('random', 'random'));
    emitter.addBehaviour(new Proton.Scale(1, 0.25));
    emitter.addBehaviour(
      new Proton.Alpha(0.8, 0, Infinity, Proton.easeInQuart)
    );
    emitter.addBehaviour(
      new Proton.Color(
        color,
        crucibleColors.getTHREEColor(),
        Infinity,
        Proton.easeOutQuart
      )
    );

    emitter.p.x = 0;
    emitter.p.y = 6;
    emitter.emit();

    return emitter;
  }

  _createSprite(loader: CrucibleLoader, spriteUrl: string, color: THREE.Color) {
    const map = loader.textureLoader.load(spriteUrl);
    const material = new THREE.SpriteMaterial({
      map: map,
      color: color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true
    });

    material.color.convertSRGBToLinear();

    return new THREE.Sprite(material);
  }
}
